import React, { createContext, useState, useEffect, useContext } from 'react';
import * as api from '../services/api';
import { getChannelContentKey, resolveContentChannel } from '../shared/channel/contentChannel';

const HeroContext = createContext();
const INITIAL_HERO_DATA = {
    tag: "RESULTADO REAL",
    title: "Não é sobre a tecnologia. É sobre o que você faz com ela.",
    summary: "Potencialize seus resultados com soluções de inteligência digital e inovação focada no que realmente importa: o seu negócio.",
    media: []
};

export const useHero = () => useContext(HeroContext);

export const HeroProvider = ({ children }) => {
    const contentChannel = resolveContentChannel();
    const targetKey = getChannelContentKey('hero', contentChannel);

    const [heroData, setHeroData] = useState(INITIAL_HERO_DATA);
    const [loading, setLoading] = useState(true);
    const [loadedFromKey, setLoadedFromKey] = useState(targetKey);

    useEffect(() => {
        const loadHeroData = async () => {
            setLoading(true);
            try {
                const { key, content } = await api.getChannelPageContent('hero', contentChannel, { fallbackToLegacy: true });
                setLoadedFromKey(key);
                const data = content;
                if (data) {
                    setHeroData({ ...INITIAL_HERO_DATA, ...data });
                } else {
                    setHeroData(INITIAL_HERO_DATA);
                }
            } catch (error) {
                console.error("Failed to load hero data:", error);
            } finally {
                setLoading(false);
            }
        };

        loadHeroData();
    }, [contentChannel, targetKey]);

    const updateHeroData = async (newData) => {
        try {
            const merged = { ...heroData, ...newData };
            const { key } = await api.saveChannelPageContent('hero', contentChannel, merged);
            setLoadedFromKey(key);
            setHeroData(merged);
        } catch (error) {
            console.error("Failed to update hero data:", error);
            throw error;
        }
    };

    const addMedia = async (mediaInput, type) => {
        // mediaInput can be an object (from upload) or string
        let newMedia = { type, id: Date.now() };
        
        if (typeof mediaInput === 'object' && mediaInput !== null) {
            newMedia = { ...newMedia, ...mediaInput }; // url, key, etc.
        } else {
            newMedia.url = mediaInput;
        }

        const newData = {
            ...heroData,
            media: [...(heroData.media || []), newMedia]
        };
        await updateHeroData(newData);
    };

    const removeMedia = async (id) => {
        const newData = {
            ...heroData,
            media: heroData.media.filter(item => item.id !== id)
        };
        await updateHeroData(newData);
    };

    return (
        <HeroContext.Provider
            value={{
                heroData,
                loading,
                updateHeroData,
                addMedia,
                removeMedia,
                contentChannel,
                contentKey: targetKey,
                loadedFromKey,
                usingLegacyFallback: loadedFromKey === 'hero'
            }}
        >
            {children}
        </HeroContext.Provider>
    );
};
